<template>
  <div class="waffle-menu-container" ref="waffleMenu">
    <button
      class="waffle-trigger"
      @click="toggleMenu()"
      :aria-label="ariaLabel"
      :aria-expanded="menuOpen ? 'true' : 'false'"
    >
      <FontAwesomeIcon icon="th" :color="buttonColor" size="2x" />
      <div v-if="menuOpen">
        <div class="waffle-triangle-black" />
        <div
          class="waffle-triangle"
          :style="{
            borderColor:
              'transparent transparent ' + menuBackgroundColor + ' transparent',
          }"
        />
      </div>
    </button>
    <ul
      class="waffle-dropdown"
      v-if="menuOpen && dataLoaded"
      :style="{ backgroundColor: menuBackgroundColor }"
    >
      <li class="waffle-dropdown-header">
        <slot name="header" />
      </li>
      <li
        class="waffle-menu-item"
        v-for="(item, index) in this.dataMenuItems"
        :key="index"
      >
        <a
          :href="item.link"
          :background="item.image"
          :target="item.targetLink"
          :rel="item.targetLink === '_blank' ? 'noopener noreferrer' : ''"
        >
          <img :src="item.image" alt="" /> <span>{{ item.label }}</span>
        </a>
      </li>
      <li class="waffle-dropdown-footer">
        <slot name="footer" />
      </li>
    </ul>
  </div>
</template>
<script>
import oidc from '@uportal/open-id-connect';
import { portletRegistryToArray } from '@uportal/portlet-registry-to-array';
import get from 'lodash/get';
// import PropTypes from 'prop-types';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faTh);

export default {
  name: 'WaffleMenu',
  components: {
    FontAwesomeIcon,
  },
  props: {
    url: {
      type: String,
      default: '/uPortal/api/v4-3/dlm/portletRegistry.json',
    },
    oidcUrl: {
      type: String,
      default: '/uPortal/api/v5-1/userinfo',
    },
    contextPortletUrl: {
      type: String,
      default: '/uPortal/p/',
    },
    buttonColor: {
      type: String,
      default: '#fff',
    },
    buttonLabel: {
      type: String,
      default: 'Waffle Menu',
    },
    menuBackgroundColor: {
      type: String,
      default: '#fff',
    },
    debug: {
      type: Boolean,
      default: false,
    },
    defaultIcon: {
      type: String,
      default:
        '/ResourceServingWebapp/rs/tango/0.8.90/32x32/categories/applications-other.png',
    },
    truncateLength: {
      type: Number,
      default: 50,
    },
  },

  computed: {
    dataMenuItems: function () {
      return this.data.filter((datum) => datum.type === 'box');
    },
    dataMenuFooter: function () {
      return this.data.filter((datum) => datum.type === 'footer');
    },
  },
  data() {
    return {
      menuOpen: false,
      ariaLabel: this.buttonLabel,
      data: [],
      dataItems: [],
      dataLoaded: false,
      hasError: false,
      errorMessage: '',
    };
  },
  methods: {
    handleOidcError(err) {
      console.error(err);
      this.hasError = true;
      this.errorMessage = 'There was a problem authorizing this request.';
    },
    handleWflError(err) {
      console.error(err);
      this.hasError = true;
      this.errorMessage = 'There was a problem cooking your waffle.';
    },
    async getToken() {
      try {
        return await oidc({ userInfoApiUrl: this.oidcUrl });
      } catch (err) {
        console.error(err);
        this.handleOidcError(err);
      }
    },
    truncateTitle(title) {
      if (title.length > this.truncateLength) {
        return title.substring(0, this.truncateLength + 1) + '...';
      }

      return title;
    },
    wafflePress(registry) {
      const menuItems = portletRegistryToArray(registry).map(
        ({ fname, parameters, title }) => {
          const imgUrl =
            get(parameters, 'waffleIconUrl.value') ||
            get(parameters, 'iconUrl.value') ||
            this.defaultIcon;
          const text = get(parameters, 'waffleLabel.value') || title;

          const alternativeMaximizedLink = get(
            parameters,
            'alternativeMaximizedLink.value'
          );

          let targetLinkValue = '_self';
          if (alternativeMaximizedLink) {
            targetLinkValue = '_blank';
          }

          return {
            link: alternativeMaximizedLink || this.contextPortletUrl + fname,
            image: imgUrl
              ? process.env.NODE_ENV === 'development'
                ? 'proxy/' + imgUrl
                : imgUrl
              : undefined,
            label: this.truncateTitle(text),
            type: 'box',
            targetLink: targetLinkValue,
          };
        }
      );

      this.data = menuItems;
      this.dataLoaded = true;
    },
    async fetchMenuData() {
      const token = this.debug ? null : (await this.getToken()).encoded;

      try {
        const response = await fetch(this.url, {
          credentials: 'same-origin',
          headers: {
            Authorization: 'Bearer ' + token,
            'content-type': 'application/jwt',
          },
        });

        if (!response.ok || response.status !== 200) {
          throw new Error(response.statusText);
        }

        const payload = await response.json();

        this.wafflePress(payload);
      } catch (err) {
        console.error(err);
      }
    },
    // toggle the menu
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    // close the menu if we're clicking outside the menu or trigger
    handleOutsideClick(event) {
      const menu = this.$refs.waffleMenu;
      const target = event.target;
      const shadow = target.shadowRoot ? target.shadowRoot : false;
      if (menu !== target && !shadow && !menu.contains(target)) {
        this.menuOpen = false;
      }
    },
    handleKeyDown(event) {
      const menu = this.$refs.waffleMenu;
      const target = event.target;
      if (
        event.key === 'Escape' &&
        this.menuOpen &&
        (menu.contains(target) ||
          event.tagName === 'WAFFLE-MENU' ||
          target.tagName === 'WAFFLE-MENU')
      ) {
        this.toggleMenu();
        menu.querySelector('.waffle-trigger').focus();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick, false);
    document.addEventListener('keydown', this.handleKeyDown, false);

    // Initialize Menu data when Mounted
    this.fetchMenuData();
  },
};
</script>
<style lang="scss">
.waffle-menu-container {
  display: flex;
  margin-left: auto;
  position: relative;
  line-height: 0;
  padding: 0;
}

.waffle-trigger {
  width: 25px;
  height: 25px;
  line-height: 0;
  padding: 0;
  margin-left: auto;
  background: transparent;
  border: 0 none;

  svg {
    padding: 0;
    margin: 0;
  }
}

.waffle-dropdown {
  position: absolute;
  top: 150%;
  right: 0;
  z-index: 1000;
  display: flex;
  float: left;
  min-width: 10rem;
  padding: 0;
  margin: 0.125rem 0 0;
  font-size: 1rem;
  color: #212529;
  text-align: left;
  list-style: none;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.05);
  width: 220px;
  flex-flow: row wrap;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
}

.waffle-menu-item {
  display: block;
  flex: 0 0 46%;
  margin: 2% 2%;
  line-height: 1rem;
  padding: 0.25rem 0;

  &:hover {
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
  }

  a {
    display: block;
    padding: 5px;
    margin: 0;
    position: relative;
    text-decoration: none;

    img {
      width: 60%;
      margin: 0 auto;
      display: block;
    }

    span {
      bottom: 0;
      display: block;
      color: #333;
      text-align: center;
      font-size: 80%;
      padding: 3px 0;
    }
  }
}

.waffle-dropdown-footer,
.waffle-dropdown-header {
  display: block;
  flex: 1 0 100%;
  line-height: 1rem;
  margin-top: 0;
  box-sizing: border-box;
  padding: 1em;
  font-size: 1.2em;
  background-color: #eee;
  background-color: var(--waffle-menu-header-footer-bg-color, #eee);

  &:last-child {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  &:first-child {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  &:empty {
    display: none;
  }

  a {
    display: block;
    text-decoration: none;
    color: #000;
    color: var(--waffle-menu-header-footer-link-color, #000);

    &:hover {
      color: #666;
      color: var(--waffle-menu-header-footer-link-hover-color, #666);
    }
  }
}

.waffle-triangle-black {
  border-color: transparent transparent #fff transparent;
  border-style: dashed dashed solid;
  border-width: 0 8.5px 8.5px;
  border-bottom-color: rgba(0, 0, 0, 0.25);
  width: 0;
  height: 0;
  position: absolute;
  top: 31px;
  right: 20px;
  z-index: 2000;
}

.waffle-triangle {
  border-color: transparent transparent #fff transparent;
  border-style: dashed dashed solid;
  border-width: 0 8.5px 8.5px;
  width: 0;
  height: 0;
  position: absolute;
  top: 32px;
  right: 20px;
  z-index: 2000;
}
</style>
